import { ChevronLast } from "lucide-react";
import { type RefObject, memo, useEffect, useState } from "react";

interface ScrollToBottomButtonProps {
  containerRef: RefObject<HTMLDivElement>;
}

const ScrollToBottomButton = memo(
  ({ containerRef }: ScrollToBottomButtonProps) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const handleScroll = () => {
        if (containerRef.current) {
          const totalHeight = containerRef.current.scrollHeight;
          const visibleHeight = containerRef.current.clientHeight;
          const scrolledHeight = containerRef.current.scrollTop;

          // Check if there is scrollable content and if the user is not already at the bottom
          if (
            totalHeight > visibleHeight &&
            scrolledHeight + visibleHeight < totalHeight - 1
          ) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        }
      };

      const container = containerRef.current;
      if (container) {
        container.addEventListener("scroll", handleScroll);

        // Check visibility on mount
        handleScroll();

        return () => {
          container.removeEventListener("scroll", handleScroll);
        };
      }
    }, [containerRef]);

    const scrollToBottom = () => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    return (
      isVisible && (
        <div className="fixed bottom-44 right-3 md:right-6 z-50 w-full flex justify-end drop-shadow-sm">
          <button
            type="button"
            onClick={scrollToBottom}
            className="bg-[#F8F8F8] dark:bg-[#242527] p-2 rounded-full"
          >
            <ChevronLast size={24} className="rotate-90 text-primaryaccent" />
          </button>
        </div>
      )
    );
  },
);

ScrollToBottomButton.displayName = "StatsBox";

export default ScrollToBottomButton;
